import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import {
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsLegend } from "@mui/x-charts";
import { format } from "date-fns";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import { LineChartPro } from "@mui/x-charts-pro";
interface Props {
  exequteQueryContentQuery5: any;
  exequteQueryContentQuery7: any;
  exequteQueryContentQuery8: any;
  loading: any;
  selectedCompetitorCompany: any;
  setSelectedCompetitorCompany: any;
  selectedGraphType: any;
  selectedCompetitorDomainName: any;
  setSelectedCompetitorDomainName: any;
}

type DynamicDataset = {
  x: string;
  [key: string]: number | string;
};

const CompetitorsPageSpeedDashboardGraph = ({
  loading,
  exequteQueryContentQuery5,
  exequteQueryContentQuery8,
  selectedCompetitorDomainName,
  exequteQueryContentQuery7,
  selectedCompetitorCompany,
  selectedGraphType,
}: Props) => {
  const selectedCompetitorNames = selectedCompetitorCompany.map(
    (item: any) => item.label
  );
  const filteredCompetitorData =
    exequteQueryContentQuery7?.query_output?.filter((item: any) =>
      selectedCompetitorNames.includes(item.competitor_company_name)
    );

  const getCompaniesData = (
    filteredCompetitorData: any[]
  ): Record<string, any[]> => {
    const groupedData = Array.isArray(filteredCompetitorData)
      ? filteredCompetitorData.reduce((acc: any, item: any) => {
          const key = item.competitor_company_name;
          if (!key) return acc;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);
          return acc;
        }, {})
      : {};
    if (groupedData && Object.keys(groupedData).length > 0) {
      return (Object.entries(groupedData) as [string, any[]][]).reduce(
        (acc: Record<string, any[]>, [companyName, companyData]) => {
          acc[companyName] = companyData || [];
          return acc;
        },
        {}
      );
    }
    return {};
  };

  const companiesData = getCompaniesData(filteredCompetitorData);

  type SeriesType = {
    type: "line";
    dataKey: string;
    color: string;
    curve?: "linear" | "step" | "monotoneX" | "monotoneY";
  };

  function extractCompetitorDataLCP(companiesData: Record<string, any[]>) {
    return Object.entries(companiesData).map(([competitorName, data]) => ({
      name: competitorName, // Name of the competitor
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_LCP || 0).toFixed(2)),
      })),
    }));
  }

  const competitorDatasetsLCP = extractCompetitorDataLCP(companiesData);

  const GraphRealUsersLCP = exequteQueryContentQuery5?.query_output?.length
    ? exequteQueryContentQuery5?.query_output?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
        y: parseFloat((item?.Avg_Real_Users_Experience_LCP || 0).toFixed(2)),
      }))
    : [{ x: "No Data", y: 0 }];

  const chartSeriesLCP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersLCP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...competitorDatasetsLCP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / competitorDatasetsLCP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allLCPDates = new Set();
  chartSeriesLCP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) => allLCPDates.add(dataPoint.x));
  });

  const sortedLCPDates = Array.from(allLCPDates).sort();

  chartSeriesLCP.forEach((series) => {
    const filledDataset = sortedLCPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const LCPDATASET = chartSeriesLCP.map((s: any) => s.dataset);
  let CombinedLCPDataset: DynamicDataset[] = [];
  const combinedDataLCP: { [key: string]: DynamicDataset } = {};

  LCPDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDataLCP[date]) {
        combinedDataLCP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDataLCP[date], item);
    });
  });

  for (const date in combinedDataLCP) {
    const entry = combinedDataLCP[date];
    CombinedLCPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysLCP = new Set();
  CombinedLCPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysLCP.add(key);
      }
    });
  });

  const seriesLCP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysLCP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysLCP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractCompetitorDataINP(companiesData: Record<string, any[]>) {
    return Object.entries(companiesData).map(([competitorName, data]) => ({
      name: competitorName, // Name of the competitor
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_INP || 0).toFixed(2)),
      })),
    }));
  }

  const competitorDatasetsINP = extractCompetitorDataINP(companiesData);

  const GraphRealUsersINP = exequteQueryContentQuery5?.query_output?.length
    ? exequteQueryContentQuery5?.query_output?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
        y: parseFloat((item?.Avg_Real_Users_Experience_INP || 0).toFixed(2)),
      }))
    : [{ x: "No Data", y: 0 }];

  const chartSeriesINP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersINP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...competitorDatasetsINP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / competitorDatasetsINP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allINPDates = new Set();
  chartSeriesINP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) => allINPDates.add(dataPoint.x));
  });

  const sortedINPDates = Array.from(allINPDates).sort();

  // Fill missing dates in each dataset
  chartSeriesINP.forEach((series) => {
    const filledDataset = sortedINPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const INPDATASET = chartSeriesINP.map((s: any) => s.dataset);
  let CombinedINPDataset: DynamicDataset[] = [];
  const combinedDataINP: { [key: string]: DynamicDataset } = {};

  INPDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDataINP[date]) {
        combinedDataINP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDataINP[date], item);
    });
  });

  for (const date in combinedDataINP) {
    const entry = combinedDataINP[date];
    // Replace null values with 0
    CombinedINPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysINP = new Set();
  CombinedINPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysINP.add(key);
      }
    });
  });

  const seriesINP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysINP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysINP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractCompetitorDataCLS(companiesData: Record<string, any[]>) {
    return Object.entries(companiesData).map(([competitorName, data]) => ({
      name: competitorName, // Name of the competitor
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_CLS || 0).toFixed(2)),
      })),
    }));
  }

  const competitorDatasetsCLS = extractCompetitorDataCLS(companiesData);

  const GraphRealUsersCLS = exequteQueryContentQuery5?.query_output?.length
    ? exequteQueryContentQuery5?.query_output?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
        y: parseFloat((item?.Avg_Real_Users_Experience_CLS || 0).toFixed(2)),
      }))
    : [{ x: "No Data", y: 0 }];

  const chartSeriesCLS = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersCLS, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...competitorDatasetsCLS.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / competitorDatasetsCLS.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allCLSDates = new Set();
  chartSeriesCLS.forEach((series) => {
    series.dataset.forEach((dataPoint: any) => allCLSDates.add(dataPoint.x));
  });

  const sortedCLSDates = Array.from(allCLSDates).sort();

  // Fill missing dates in each dataset
  chartSeriesCLS.forEach((series) => {
    const filledDataset = sortedCLSDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const CLSDATASET = chartSeriesCLS.map((s: any) => s.dataset);
  let CombinedCLSDataset: DynamicDataset[] = [];
  const combinedDataCLS: { [key: string]: DynamicDataset } = {};

  CLSDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDataCLS[date]) {
        combinedDataCLS[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDataCLS[date], item);
    });
  });

  for (const date in combinedDataCLS) {
    const entry = combinedDataCLS[date];
    // Replace null values with 0
    CombinedCLSDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  // Extract unique keys for dynamic series creation
  const dynamicKeysCLS = new Set();
  CombinedCLSDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysCLS.add(key);
      }
    });
  });

  // Create series based on dynamic keys
  const seriesCLS: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysCLS).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysCLS.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractCompetitorDataFCP(companiesData: Record<string, any[]>) {
    return Object.entries(companiesData).map(([competitorName, data]) => ({
      name: competitorName, // Name of the competitor
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_FCP || 0).toFixed(2)),
      })),
    }));
  }

  // Extract dynamic datasets for all competitors
  const competitorDatasetsFCP = extractCompetitorDataFCP(companiesData);

  const GraphRealUsersFCP = exequteQueryContentQuery5?.query_output?.length
    ? exequteQueryContentQuery5?.query_output?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
        y: parseFloat((item?.Avg_Real_Users_Experience_FCP || 0).toFixed(2)),
      }))
    : [{ x: "No Data", y: 0 }];

  const chartSeriesFCP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersFCP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...competitorDatasetsFCP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / competitorDatasetsFCP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allFCPDates = new Set();
  chartSeriesFCP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) => allFCPDates.add(dataPoint.x));
  });

  const sortedFCPDates = Array.from(allFCPDates).sort();

  // Fill missing dates in each dataset
  chartSeriesFCP.forEach((series) => {
    const filledDataset = sortedFCPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const FCPDATASET = chartSeriesFCP.map((s: any) => s.dataset);
  let CombinedFCPDataset: DynamicDataset[] = [];
  const combinedDataFCP: { [key: string]: DynamicDataset } = {};

  FCPDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDataFCP[date]) {
        combinedDataFCP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDataFCP[date], item);
    });
  });

  for (const date in combinedDataFCP) {
    const entry = combinedDataFCP[date];
    CombinedFCPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysFCP = new Set();
  CombinedFCPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysFCP.add(key);
      }
    });
  });

  const seriesFCP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysFCP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysFCP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractCompetitorDataTTFP(companiesData: Record<string, any[]>) {
    return Object.entries(companiesData).map(([competitorName, data]) => ({
      name: competitorName, // Name of the competitor
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_TTFB || 0).toFixed(2)),
      })),
    }));
  }

  const competitorDatasetsTTFB = extractCompetitorDataTTFP(companiesData);

  const GraphRealUsersTTFB = exequteQueryContentQuery5?.query_output?.length
    ? exequteQueryContentQuery5?.query_output?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
        y: parseFloat((item?.Avg_Real_Users_Experience_TTFB || 0).toFixed(2)),
      }))
    : [{ x: "No Data", y: 0 }];

  const chartSeriesTTFB = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersTTFB, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...competitorDatasetsTTFB.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / competitorDatasetsTTFB.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allTTFBDates = new Set();
  chartSeriesTTFB.forEach((series) => {
    series.dataset.forEach((dataPoint: any) => allTTFBDates.add(dataPoint.x));
  });

  const sortedTTFBDates = Array.from(allTTFBDates).sort();

  chartSeriesTTFB.forEach((series) => {
    const filledDataset = sortedTTFBDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const TTFBDATASET = chartSeriesTTFB.map((s: any) => s.dataset);
  let CombinedTTFBDataset: DynamicDataset[] = [];
  const combinedDataTTFB: { [key: string]: DynamicDataset } = {};

  TTFBDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDataTTFB[date]) {
        combinedDataTTFB[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDataTTFB[date], item);
    });
  });

  for (const date in combinedDataTTFB) {
    const entry = combinedDataTTFB[date];
    // Replace null values with 0
    CombinedTTFBDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysTTFB = new Set();
  CombinedTTFBDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysTTFB.add(key);
      }
    });
  });

  const seriesTTFB: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysTTFB).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysTTFB.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  const selectedCompetitorDomainNames = selectedCompetitorDomainName.map(
    (item: any) => item.label
  );
  const filteredCompetitorDomainData =
    exequteQueryContentQuery8?.query_output?.filter((item: any) =>
      selectedCompetitorDomainNames.includes(item.competitor_domain)
    );
  const getDomainsData = (
    filteredCompetitorDomainData: any[]
  ): Record<string, any[]> => {
    const groupedData = Array.isArray(filteredCompetitorDomainData)
      ? filteredCompetitorDomainData.reduce((acc: any, item: any) => {
          const key = item.competitor_domain;
          if (!key) return acc;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);
          return acc;
        }, {})
      : {};

    if (groupedData && Object.keys(groupedData).length > 0) {
      return (Object.entries(groupedData) as [string, any[]][]).reduce(
        (acc: Record<string, any[]>, [domainName, domainData]) => {
          acc[domainName] = domainData || [];
          return acc;
        },
        {}
      );
    }
    return {};
  };

  const DomainData = getDomainsData(filteredCompetitorDomainData);

  function extractDomainDataLCP(DomainData: Record<string, any[]>) {
    return Object.entries(DomainData).map(([domainName, data]) => ({
      name: domainName,
      companyName: data[0]?.competitor_company_name || "Unknown Company",
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_LCP || 0).toFixed(2)),
      })),
    }));
  }

  const domainDatasetsLCP = extractDomainDataLCP(DomainData);

  const domainchartSeriesLCP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersLCP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...domainDatasetsLCP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / domainDatasetsLCP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allDomainLCPDates = new Set();
  domainchartSeriesLCP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) =>
      allDomainLCPDates.add(dataPoint.x)
    );
  });

  const sortedDomainLCPDates = Array.from(allDomainLCPDates).sort();

  domainchartSeriesLCP.forEach((series) => {
    const filledDataset = sortedDomainLCPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const LCPDOMAINDATASET = domainchartSeriesLCP.map((s: any) => s.dataset);
  let CombinedDomainLCPDataset: DynamicDataset[] = [];
  const combinedDomainDataLCP: { [key: string]: DynamicDataset } = {};

  LCPDOMAINDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDomainDataLCP[date]) {
        combinedDomainDataLCP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDomainDataLCP[date], item);
    });
  });

  for (const date in combinedDomainDataLCP) {
    const entry = combinedDomainDataLCP[date];
    CombinedDomainLCPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysDomainLCP = new Set();
  CombinedDomainLCPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysDomainLCP.add(key);
      }
    });
  });

  const seriesDomainLCP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysDomainLCP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysDomainLCP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractDomainDataINP(DomainData: Record<string, any[]>) {
    return Object.entries(DomainData).map(([domainName, data]) => ({
      name: domainName,
      companyName: data[0]?.competitor_company_name || "Unknown Company",
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_INP || 0).toFixed(2)),
      })),
    }));
  }

  const domainDatasetsINP = extractDomainDataINP(DomainData);

  const domainchartSeriesINP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersINP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...domainDatasetsINP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / domainDatasetsINP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allDomainINPDates = new Set();
  domainchartSeriesINP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) =>
      allDomainINPDates.add(dataPoint.x)
    );
  });

  const sortedDomainINPDates = Array.from(allDomainINPDates).sort();

  domainchartSeriesINP.forEach((series) => {
    const filledDataset = sortedDomainINPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const INPDOMAINDATASET = domainchartSeriesINP.map((s: any) => s.dataset);
  let CombinedDomainINPDataset: DynamicDataset[] = [];
  const combinedDomainDataINP: { [key: string]: DynamicDataset } = {};

  INPDOMAINDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDomainDataINP[date]) {
        combinedDomainDataINP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDomainDataINP[date], item);
    });
  });

  for (const date in combinedDomainDataINP) {
    const entry = combinedDomainDataINP[date];
    CombinedDomainINPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysDomainINP = new Set();
  CombinedDomainINPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysDomainINP.add(key);
      }
    });
  });

  const seriesDomainINP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysDomainINP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysDomainINP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractDomainDataCLS(DomainData: Record<string, any[]>) {
    return Object.entries(DomainData).map(([domainName, data]) => ({
      name: domainName,
      companyName: data[0]?.competitor_company_name || "Unknown Company",
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_CLS || 0).toFixed(2)),
      })),
    }));
  }

  const domainDatasetsCLS = extractDomainDataCLS(DomainData);

  const domainchartSeriesCLS = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersCLS, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...domainDatasetsCLS.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / domainDatasetsCLS.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allDomainCLSDates = new Set();
  domainchartSeriesCLS.forEach((series) => {
    series.dataset.forEach((dataPoint: any) =>
      allDomainCLSDates.add(dataPoint.x)
    );
  });

  const sortedDomainCLSDates = Array.from(allDomainCLSDates).sort();

  domainchartSeriesCLS.forEach((series) => {
    const filledDataset = sortedDomainCLSDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const CLSDOMAINDATASET = domainchartSeriesCLS.map((s: any) => s.dataset);
  let CombinedDomainCLSDataset: DynamicDataset[] = [];
  const combinedDomainDataCLS: { [key: string]: DynamicDataset } = {};

  CLSDOMAINDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDomainDataCLS[date]) {
        combinedDomainDataCLS[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDomainDataCLS[date], item);
    });
  });

  for (const date in combinedDomainDataCLS) {
    const entry = combinedDomainDataCLS[date];
    CombinedDomainCLSDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysDomainCLS = new Set();
  CombinedDomainCLSDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysDomainCLS.add(key);
      }
    });
  });

  const seriesDomainCLS: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysDomainCLS).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysDomainCLS.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractDomainDataFCP(DomainData: Record<string, any[]>) {
    return Object.entries(DomainData).map(([domainName, data]) => ({
      name: domainName,
      companyName: data[0]?.competitor_company_name || "Unknown Company",
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_FCP || 0).toFixed(2)),
      })),
    }));
  }

  const domainDatasetsFCP = extractDomainDataFCP(DomainData);

  const domainchartSeriesFCP = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersFCP, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...domainDatasetsFCP.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / domainDatasetsFCP.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allDomainFCPDates = new Set();
  domainchartSeriesFCP.forEach((series) => {
    series.dataset.forEach((dataPoint: any) =>
      allDomainFCPDates.add(dataPoint.x)
    );
  });

  const sortedDomainFCPDates = Array.from(allDomainFCPDates).sort();

  domainchartSeriesFCP.forEach((series) => {
    const filledDataset = sortedDomainFCPDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const FCPDOMAINDATASET = domainchartSeriesFCP.map((s: any) => s.dataset);
  let CombinedDomainFCPDataset: DynamicDataset[] = [];
  const combinedDomainDataFCP: { [key: string]: DynamicDataset } = {};

  FCPDOMAINDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDomainDataFCP[date]) {
        combinedDomainDataFCP[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDomainDataFCP[date], item);
    });
  });

  for (const date in combinedDomainDataFCP) {
    const entry = combinedDomainDataFCP[date];
    CombinedDomainFCPDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysDomainFCP = new Set();
  CombinedDomainFCPDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysDomainFCP.add(key);
      }
    });
  });

  const seriesDomainFCP: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysDomainFCP).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysDomainFCP.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  function extractDomainDataTTFB(DomainData: Record<string, any[]>) {
    return Object.entries(DomainData).map(([domainName, data]) => ({
      name: domainName,
      companyName: data[0]?.competitor_company_name || "Unknown Company",
      dataset: data.map((entry: any) => ({
        x: format(new Date(entry?.date), "dd-MM-yyyy") || "No Date",
        y: parseFloat((entry?.Avg_Real_Users_Experience_TTFB || 0).toFixed(2)),
      })),
    }));
  }

  const domainDatasetsTTFB = extractDomainDataTTFB(DomainData);

  const domainchartSeriesTTFB = [
    {
      dataKey: "y", // Main dataset key for "Your Data"
      color: "#6200EE", // Primary series color
      showMark: false,
      curve: "linear" as const, // Ensure CurveType is correctly cast
      dataset: GraphRealUsersTTFB, // Original dataset
      name: "Your Data", // Label for the primary series
    },
    ...domainDatasetsTTFB.map((competitor, index) => ({
      dataKey: `y_${index}`, // Dynamically create a unique key for each competitor
      dataset: competitor.dataset.map((entry: any) => ({
        x: entry.x,
        [`y_${index}`]: entry.y, // Dynamically name the y-axis key based on the index
      })),
      color: `hsl(${(index * 360) / domainDatasetsTTFB.length}, 70%, 50%)`, // Unique color per competitor
      showMark: false,
      curve: "linear" as const, // Ensure consistent line style
      name: competitor.name, // Use competitor name for the legend
    })),
  ];

  const allDomainTTFBDates = new Set();
  domainchartSeriesTTFB.forEach((series) => {
    series.dataset.forEach((dataPoint: any) =>
      allDomainTTFBDates.add(dataPoint.x)
    );
  });

  const sortedDomainTTFBDates = Array.from(allDomainTTFBDates).sort();

  domainchartSeriesTTFB.forEach((series) => {
    const filledDataset = sortedDomainTTFBDates.map((date) => {
      const dataPoint = series.dataset.find((dp: any) => dp.x === date);
      if (dataPoint) {
        return dataPoint; // Use existing data point
      }
      return { x: date, [series.dataKey]: 0 };
    });
    series.dataset = filledDataset;
  });

  const TTFBDOMAINDATASET = domainchartSeriesTTFB.map((s: any) => s.dataset);
  let CombinedDomainTTFBDataset: DynamicDataset[] = [];
  const combinedDomainDataTTFB: { [key: string]: DynamicDataset } = {};

  TTFBDOMAINDATASET.forEach((dataset) => {
    dataset.forEach((item: any) => {
      const date = item.x; // Get the date
      if (!combinedDomainDataTTFB[date]) {
        combinedDomainDataTTFB[date] = { x: date }; // Initialize the object with the date
      }
      Object.assign(combinedDomainDataTTFB[date], item);
    });
  });

  for (const date in combinedDomainDataTTFB) {
    const entry = combinedDomainDataTTFB[date];
    CombinedDomainTTFBDataset.push({
      x: entry.x,
      ...Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [
          key,
          value !== null ? value : 0,
        ])
      ),
    });
  }

  const dynamicKeysDomainTTFB = new Set();
  CombinedDomainTTFBDataset.forEach((data) => {
    Object.keys(data).forEach((key) => {
      if (key !== "x" && key !== "y") {
        dynamicKeysDomainTTFB.add(key);
      }
    });
  });

  const seriesDomainTTFB: SeriesType[] = [
    { type: "line", dataKey: "y", color: "#6200EE", curve: "linear" }, // Main dataset
    ...Array.from(dynamicKeysDomainTTFB).map((key, index) => ({
      type: "line" as const,
      dataKey: key as string,
      color: `hsl(${(index * 360) / dynamicKeysDomainTTFB.size}, 70%, 50%)`, // Generate a unique color for each dynamic key
      curve: "linear" as const,
    })),
  ];

  const sortDatasetByDate = (dataset: DynamicDataset[]) => {
    return dataset.sort((a, b) => {
      const dateA = new Date(a.x.split("-").reverse().join("-"));
      const dateB = new Date(b.x.split("-").reverse().join("-"));
      return dateA.getTime() - dateB.getTime();
    });
  };

  CombinedLCPDataset = sortDatasetByDate(CombinedLCPDataset);
  CombinedINPDataset = sortDatasetByDate(CombinedINPDataset);
  CombinedCLSDataset = sortDatasetByDate(CombinedCLSDataset);
  CombinedFCPDataset = sortDatasetByDate(CombinedFCPDataset);
  CombinedTTFBDataset = sortDatasetByDate(CombinedTTFBDataset);
  CombinedDomainLCPDataset = sortDatasetByDate(CombinedDomainLCPDataset);
  CombinedDomainINPDataset = sortDatasetByDate(CombinedDomainINPDataset);
  CombinedDomainCLSDataset = sortDatasetByDate(CombinedDomainCLSDataset);
  CombinedDomainFCPDataset = sortDatasetByDate(CombinedDomainFCPDataset);
  CombinedDomainTTFBDataset = sortDatasetByDate(CombinedDomainTTFBDataset);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Grid container rowSpacing={3} columnSpacing={3} mt={1} mb={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "18px", color: "#000000" }}>
              LCP (Largest Contentful Paint)
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
              in Seconds
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <LineChartPro
                  dataset={
                    selectedGraphType === "competitor"
                      ? CombinedLCPDataset
                      : CombinedDomainLCPDataset
                  }
                  series={
                    selectedGraphType === "competitor"
                      ? seriesLCP
                      : seriesDomainLCP
                  }
                  xAxis={[{ scaleType: "point", dataKey: "x" }]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      position: "right",
                    },
                  ]}
                  height={400}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                    },
                    [`& .${markElementClasses.root}`]: {
                      scale: "0.6",
                      strokeWidth: 2,
                      "&:hover": {
                        scale: "1.2",
                        strokeWidth: 5,
                        opacity: "0.6",
                      },
                    },
                  }}
                  leftAxis={null}
                  rightAxis={{}}
                >
                  <ChartsGrid horizontal />
                  <ChartsXAxis />
                  <ChartsYAxis />
                  <LinePlot />
                  <MarkPlot />
                  <CustomItemTooltip />
                  <ChartsLegend direction="row" />
                </LineChartPro>
                <Grid container spacing={2}>
                  <Grid item xs="auto">
                    {" "}
                    {/* Width adjusts to content */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 3,
                          backgroundColor: "#6200EE",
                          mr: 1,
                        }}
                      ></Box>
                      <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                        Optival
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedGraphType === "competitor"
                    ? competitorDatasetsLCP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {" "}
                            {/* Unique key for each item */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    chartSeriesLCP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )
                    : domainDatasetsLCP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {" "}
                            {/* Unique key for each item */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    domainchartSeriesLCP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name} ({competitors?.companyName}){" "}
                                {/* Company Name */}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                </Grid>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "18px", color: "#000000" }}>
              INP (Interaction to Next Paint)
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
              in Milliseconds
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <LineChartPro
                  dataset={
                    selectedGraphType === "competitor"
                      ? CombinedINPDataset
                      : CombinedDomainINPDataset
                  }
                  series={
                    selectedGraphType === "competitor"
                      ? seriesINP
                      : seriesDomainINP
                  }
                  xAxis={[{ scaleType: "point", dataKey: "x" }]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      position: "right",
                    },
                  ]}
                  height={400}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                    },
                    [`& .${markElementClasses.root}`]: {
                      scale: "0.6",
                      strokeWidth: 2,
                      "&:hover": {
                        scale: "1.2",
                        strokeWidth: 5,
                        opacity: "0.6",
                      },
                    },
                  }}
                  leftAxis={null}
                  rightAxis={{}}
                >
                  <ChartsGrid horizontal />
                  <CustomItemTooltip />
                  <ChartsXAxis />
                  <ChartsYAxis />
                  <LinePlot />
                  <MarkPlot />
                  <ChartsLegend direction="row" />
                </LineChartPro>
                <Grid container spacing={2}>
                  <Grid item xs="auto">
                    {/* First static item */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 3,
                          backgroundColor: "#6200EE",
                          mr: 1,
                        }}
                      ></Box>
                      <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                        Optival
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedGraphType === "competitor"
                    ? competitorDatasetsINP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Dynamic items for competitor */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    chartSeriesINP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )
                    : domainDatasetsINP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Dynamic items for domain */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    domainchartSeriesINP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name} ({competitors?.companyName})
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                </Grid>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "18px", color: "#000000" }}>
              CLS (Cumulative Layout Shift)
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
              in Milliseconds
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <LineChartPro
                  dataset={
                    selectedGraphType === "competitor"
                      ? CombinedCLSDataset
                      : CombinedDomainCLSDataset
                  }
                  series={
                    selectedGraphType === "competitor"
                      ? seriesCLS
                      : seriesDomainCLS
                  }
                  xAxis={[{ scaleType: "point", dataKey: "x" }]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      position: "right",
                    },
                  ]}
                  height={400}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                    },
                    [`& .${markElementClasses.root}`]: {
                      scale: "0.6",
                      strokeWidth: 2,
                      "&:hover": {
                        scale: "1.2",
                        strokeWidth: 5,
                        opacity: "0.6",
                      },
                    },
                  }}
                  leftAxis={null}
                  rightAxis={{}}
                >
                  <ChartsGrid horizontal />
                  <CustomItemTooltip />
                  <ChartsXAxis />
                  <ChartsYAxis />
                  <LinePlot />
                  <MarkPlot />
                  <ChartsLegend direction="row" />
                </LineChartPro>
                <Grid container spacing={2}>
                  <Grid item xs="auto">
                    {/* First static item */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 3,
                          backgroundColor: "#6200EE",
                          mr: 1,
                        }}
                      ></Box>
                      <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                        Optival
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedGraphType === "competitor"
                    ? competitorDatasetsCLS?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Dynamic items for competitor */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    chartSeriesCLS[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )
                    : domainDatasetsCLS?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Dynamic items for domain */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    domainchartSeriesCLS[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name} ({competitors?.companyName})
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                </Grid>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "18px", color: "#000000" }}>
              FCP (First Contentful Paint)
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
              in Seconds
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <LineChartPro
                  dataset={
                    selectedGraphType === "competitor"
                      ? CombinedFCPDataset
                      : CombinedDomainFCPDataset
                  }
                  series={
                    selectedGraphType === "competitor"
                      ? seriesFCP
                      : seriesDomainFCP
                  }
                  xAxis={[{ scaleType: "point", dataKey: "x" }]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      position: "right",
                    },
                  ]}
                  height={400}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                    },
                    [`& .${markElementClasses.root}`]: {
                      scale: "0.6",
                      strokeWidth: 2,
                      "&:hover": {
                        scale: "1.2",
                        strokeWidth: 5,
                        opacity: "0.6",
                      },
                    },
                  }}
                  leftAxis={null}
                  rightAxis={{}}
                >
                  <ChartsGrid horizontal />
                  <CustomItemTooltip />
                  <ChartsXAxis />
                  <ChartsYAxis />
                  <LinePlot />
                  <MarkPlot />
                  <ChartsLegend direction="row" />
                </LineChartPro>
                <Grid container spacing={2}>
                  <Grid item xs="auto">
                    {/* Static Optival Item */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 3,
                          backgroundColor: "#6200EE",
                          mr: 1,
                        }}
                      ></Box>
                      <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                        Optival
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedGraphType === "competitor"
                    ? competitorDatasetsFCP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Competitor Items */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    chartSeriesFCP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )
                    : domainDatasetsFCP?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Domain Items */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    domainchartSeriesFCP[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name} ({competitors?.companyName})
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                </Grid>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "18px", color: "#000000" }}>
              TTFB (Time to First Byte)
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
              in Seconds
            </Typography>
            <Stack sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <LineChartPro
                  dataset={
                    selectedGraphType === "competitor"
                      ? CombinedTTFBDataset
                      : CombinedDomainTTFBDataset
                  }
                  series={
                    selectedGraphType === "competitor"
                      ? seriesTTFB
                      : seriesDomainTTFB
                  }
                  xAxis={[{ scaleType: "point", dataKey: "x" }]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      position: "right",
                    },
                  ]}
                  height={400}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      strokeWidth: 2,
                    },
                    [`& .${markElementClasses.root}`]: {
                      scale: "0.6",
                      strokeWidth: 2,
                      "&:hover": {
                        scale: "1.2",
                        strokeWidth: 5,
                        opacity: "0.6",
                      },
                    },
                  }}
                  leftAxis={null}
                  rightAxis={{}}
                >
                  <ChartsGrid horizontal />
                  <CustomItemTooltip />
                  <ChartsXAxis />
                  <ChartsYAxis />
                  <LinePlot />
                  <MarkPlot />
                  <ChartsLegend direction="row" />
                </LineChartPro>
                <Grid container spacing={2}>
                  <Grid item xs="auto">
                    {/* Static Optival Item */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 3,
                          backgroundColor: "#6200EE",
                          mr: 1,
                        }}
                      ></Box>
                      <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                        Optival
                      </Typography>
                    </Box>
                  </Grid>
                  {selectedGraphType === "competitor"
                    ? competitorDatasetsTTFB?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Competitor Items */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    chartSeriesTTFB[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )
                    : domainDatasetsTTFB?.map(
                        (competitors: any, index: number) => (
                          <Grid item xs="auto" key={index}>
                            {/* Domain Items */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 3,
                                  backgroundColor: `${
                                    domainchartSeriesTTFB[index + 1]?.color
                                  }`,
                                  mr: 1,
                                }}
                              ></Box>
                              <Typography
                                sx={{ color: "#00000099", fontSize: "12px" }}
                              >
                                {competitors?.name} ({competitors?.companyName})
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                </Grid>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CompetitorsPageSpeedDashboardGraph;
